import { useState, type FormEvent, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { googleSignInUrl, requestMagicLink } from '@/lib/api';
import { useAuthMethods } from '@/hooks/use-auth-methods';
import { BrandMark } from '@/components/brand/brand-mark';
import { LanguageToggle } from '@/components/language-toggle';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { Button } from '@/components/ui/button';

const SIGN_IN_ERRORS = [
  'google_unavailable',
  'google_failed',
  'email_mismatch',
  'no_account',
  'link_invalid',
  'magic_link_disabled',
] as const;

export function errorCopyKey(raw: string | null): CopyKey | null {
  if (raw === null) return null;
  const known = SIGN_IN_ERRORS.find((code) => code === raw);
  return known === undefined ? 'errorGeneric' : (`error_${known}` as CopyKey);
}

export function LoginPage(): ReactElement {
  const { t, language } = useTranslation();
  const [params] = useSearchParams();
  const { googleEnabled, magicLinkEnabled } = useAuthMethods();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const failure = errorCopyKey(params.get('error'));

  function submit(event: FormEvent): void {
    event.preventDefault();
    setSent(true);
    void requestMagicLink(email, language).catch(() => undefined);
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-6 p-6 px-safe pt-safe pb-safe">
      <div className="absolute top-3 right-3 mt-safe">
        <LanguageToggle />
      </div>
      <BrandMark className="size-16 text-primary" />
      <div className="text-center">
        <h1 className="text-xl font-bold tracking-tight">{t('brand')}</h1>
        <p className="text-sm text-muted-foreground">{t('tagline')}</p>
        <p className="mt-3 max-w-xs text-sm text-muted-foreground">{t('signInLead')}</p>
      </div>

      {failure !== null && (
        <p
          role="alert"
          className="max-w-xs rounded-lg bg-red-100 px-3 py-2 text-center text-sm text-red-800"
        >
          {t(failure)}
        </p>
      )}

      <div className="flex w-full max-w-xs flex-col gap-4">
        {googleEnabled && (
          <Button asChild className="w-full">
            <a href={googleSignInUrl()}>{t('continueGoogle')}</a>
          </Button>
        )}

        {!googleEnabled && !magicLinkEnabled && (
          <p
            role="alert"
            className="rounded-lg bg-amber-100 px-3 py-2 text-center text-sm text-amber-900"
          >
            {t('noSignInMethod')}
          </p>
        )}

        {magicLinkEnabled && (
          <form className="flex flex-col gap-2" onSubmit={submit}>
            <label className="text-xs font-semibold text-muted-foreground" htmlFor="staff-email">
              {googleEnabled ? t('orEmail') : t('emailLabel')}
            </label>
            <input
              id="staff-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-base"
            />
            <Button type="submit" variant={googleEnabled ? 'outline' : 'default'}>
              {t('sendLink')}
            </Button>
          </form>
        )}

        {sent && (
          <p role="status" className="text-center text-sm text-muted-foreground">
            {t('linkSent')}
          </p>
        )}
      </div>
    </div>
  );
}
